/**
 * Workflow Selector Field Component
 * 
 * Renders a dropdown select for choosing workflows that have workflow trigger nodes
 */

import { useEffect, useState } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { HelpCircle, Workflow as WorkflowIcon } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { workflowApi } from '@/lib/api';
import type { Workflow } from '@shared/types/workflow';
import type { InferredPropertySchema } from '@/types/inferred-schema';
import { Skeleton } from '@/components/ui/skeleton';

interface WorkflowSelectorFieldProps {
  property: InferredPropertySchema;
  value: string;
  onChange: (value: string) => void;
  error?: string;
}

export function WorkflowSelectorField({ property, value, onChange, error }: WorkflowSelectorFieldProps) {
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    const fetchWorkflows = async () => {
      try {
        setLoading(true);
        setLoadError(null);
        
        // Fetch all workflows (list endpoint may not include full definition)
        const response = (await workflowApi.list()).data;
        const allWorkflows = response.items || response; // Handle paginated result
        
        // Check if workflows have definition with nodes
        const hasDefinitions = allWorkflows.some(w => w.definition?.nodes);
        
        if (!hasDefinitions) {
          
          // Fetch full details for each workflow
          const workflowsWithDetails = await Promise.all(
            allWorkflows.map(async (workflow) => {
              try {
                const fullWorkflow = await workflowApi.getById(workflow.id);
                
                // The API returns the workflow directly in response.data, not in response.data.workflow
                const workflowData = fullWorkflow?.data;
                
                if (workflowData && workflowData.definition?.nodes) {
                } else {
                }
                return workflowData;
              } catch (err) {
                // console.error(`❌ [WorkflowSelector] Failed to fetch workflow "${workflow.name}" (${workflow.id}):`, err);
                return null; // Return null if fetch fails
              }
            })
          );
          
          const successCount = workflowsWithDetails.filter(w => w !== null).length;
          
          // Filter workflows that have a workflow trigger node
          const workflowsWithTrigger = workflowsWithDetails.filter(workflow => {
            // Skip null/undefined workflows
            if (!workflow || !workflow.definition?.nodes) {
              return false;
            }
            
            // console.log(`🔍 [WorkflowSelector] Checking workflow "${workflow.name}":`, {
            //   nodeCount: workflow.definition.nodes.length,
            //   nodes: workflow.definition.nodes.map((n: any) => ({
            //     id: n.id,
            //     type: n.type,
            //     dataNodeType: n.data?.nodeType,
            //     category: n.data?.category
            //   }))
            // });
            
            // Check if workflow has at least one execute-workflow trigger node
            const hasTrigger = workflow.definition.nodes.some(
              (node: any) => {
                const matches = node.type === 'execute-workflow' || node.data?.nodeType === 'execute-workflow';
                if (matches) {
                  // console.log(`✅ [WorkflowSelector] Found trigger node in "${workflow.name}":`, {
                  //   nodeId: node.id,
                  //   type: node.type,
                  //   dataNodeType: node.data?.nodeType
                  // });
                }
                return matches;
              }
            );
            
            return hasTrigger;
          });
          
          setWorkflows(workflowsWithTrigger);
        } else {
          // List API includes definitions, filter directly
          const workflowsWithTrigger = allWorkflows.filter(workflow => {
            if (!workflow.definition?.nodes) return false;
            
            return workflow.definition.nodes.some(
              (node: any) => node.type === 'execute-workflow' || node.data?.nodeType === 'execute-workflow'
            );
          });
          
          setWorkflows(workflowsWithTrigger);
        }
      } catch (err) {
        // console.error('❌ [WorkflowSelector] Failed to fetch workflows:', err);
        // console.error('❌ [WorkflowSelector] Error details:', {
        //   message: err instanceof Error ? err.message : String(err),
        //   stack: err instanceof Error ? err.stack : undefined,
        //   error: err
        // });
        setLoadError(`Failed to load workflows: ${err instanceof Error ? err.message : String(err)}`);
      } finally {
        setLoading(false);
      }
    };

    fetchWorkflows();
  }, []);

  // Find the selected workflow name for display
  const selectedWorkflow = workflows.find(w => w.id === value);

  if (loading) {
    return (
      <div className="space-y-2">
        <Label>{property.label}</Label>
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Label htmlFor={property.key}>
          {property.label}
          {property.required && <span className="text-destructive ml-1">*</span>}
        </Label>
        {property.description && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p className="text-sm">{property.description}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      
      {loadError && (
        <p className="text-sm text-destructive">{loadError}</p>
      )}
      
      {!loadError && workflows.length === 0 && (
        <div className="text-sm text-muted-foreground p-4 border border-dashed rounded-md">
          <p className="font-medium mb-1">No workflows with Workflow Trigger found</p>
          <p className="text-xs">
            To use this node, create a child workflow with a <strong>Workflow Trigger</strong> node 
            (found in the Triggers category).
          </p>
        </div>
      )}
      
      {!loadError && workflows.length > 0 && (
        <Select
          value={value || ''}
          onValueChange={onChange}
        >
          <SelectTrigger className={error ? 'border-destructive' : ''}>
            <SelectValue placeholder={property.placeholder || 'Select a workflow'}>
              {selectedWorkflow && (
                <div className="flex items-center gap-2">
                  <WorkflowIcon className="h-4 w-4 text-purple-500" />
                  <span>{selectedWorkflow.name}</span>
                </div>
              )}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {workflows.map((workflow) => (
              <SelectItem key={workflow.id} value={workflow.id}>
                <div className="flex items-center gap-2">
                  <WorkflowIcon className="h-4 w-4 text-purple-500" />
                  <div className="flex flex-col">
                    <span>{workflow.name}</span>
                    {workflow.description && (
                      <span className="text-xs text-muted-foreground">
                        {workflow.description}
                      </span>
                    )}
                  </div>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      
      {error && <p className="text-sm text-destructive">{error}</p>}
      
      {value && selectedWorkflow && (
        <div className="text-xs text-muted-foreground">
          Workflow ID: <code className="bg-muted px-1 py-0.5 rounded">{value}</code>
        </div>
      )}
    </div>
  );
}
